/**
 * Client-side model of a site's layout: its published revisions and, when one
 * is open, its draft. The shapes are the designer's view of the `siteLayout`
 * GraphQL surface (`SiteLayout`, `Zone`, `Entry`, `EntrySide`,
 * `ReaderAssignment`), flattened so the UI can compare and diff revisions
 * without walking nested objects.
 */

export type EntrySideKind = 'ZONE' | 'OUTSIDE';

/**
 * How a crossing into a side is treated: access checked, recorded only, or
 * not tracked at all.
 */
export type Enforcement = 'ENFORCED' | 'IDENTIFY_ONLY' | 'NOT_TRACKED';

export type EntrySidePosition = 'A' | 'B';

export type Zone = {
  id: string;
  name: string;
  /**
   * The access groups granted this zone, versioned with the layout because
   * the zone is. M4 brings them; until then the API sends none.
   */
  accessGroupIds?: string[];
};

export type EntrySide = {
  id: string;
  position: EntrySidePosition;
  kind: EntrySideKind;
  /** Set when `kind` is `ZONE`; null when the side faces the outside world. */
  zoneId: string | null;
  enforcement: Enforcement;
};

export type Entry = {
  id: string;
  name: string;
  sideA: EntrySide;
  sideB: EntrySide;
};

/** A reader placed on an entry, admitting into one of its sides. */
export type ReaderAssignment = {
  id: string;
  /** The site's reader, which the layout commands name. */
  readerId: string;
  /** The reader device behind it, which the device inventory names. */
  readerDeviceId: string;
  /** The name the layout API gives the reader. */
  readerName: string;
  entryId: string;
  admitsIntoSideId: string;
};

export type Layout = {
  zones: Zone[];
  entries: Entry[];
  readerAssignments: ReaderAssignment[];
};

export type SiteLayoutDraft = Layout & {
  revisionId: string;
  /** The server's number for the version the draft would publish as. */
  number: number;
  /** The draft token the next write sends, read before this layout. */
  draftToken: number;
  /** The request id of the last client write the draft committed; null until one does. */
  lastRequestId: string | null;
};

export type SiteLayoutRevision = Layout & {
  id: string;
  /** The server's number for it: its place among the site's published revisions, from 1. */
  number: number;
  publishedAt: string;
  /** Who published it, as the actor is named in records; absent when the API does not say. */
  publishedBy?: string;
  /** Null while this is the effective revision. */
  closedAt: string | null;
};

export type SiteLayoutState = {
  draft: SiteLayoutDraft | null;
  /** Oldest first; the last one is the effective layout when present. */
  revisions: SiteLayoutRevision[];
};

/** A zone as one save writes it: renamed when it has an id, created when not. */
export type SaveZoneInput = {
  zoneId: string | null;
  name: string;
};

/** One side of an entry as one save writes it, with the reader devices admitting into it. */
export type SaveEntrySide = Pick<
  EntrySide,
  'kind' | 'zoneId' | 'enforcement'
> & {
  readerDeviceIds: string[];
};

/** An entry as one save writes it whole: rewritten when it has an id, created when not. */
export type SaveEntryInput = {
  entryId: string | null;
  name: string;
  sideA: SaveEntrySide;
  sideB: SaveEntrySide;
};

/** A reader an entry save took off another entry of the draft. */
export type MovedReader = {
  readerDeviceId: string;
  fromEntryId: string;
};

export type SavedEntry = {
  movedReaders: MovedReader[];
};

/** The zone a gateway's seeded entry leads into from the outside. */
export type GatewayDestination = {
  gatewayId: string;
  zoneId: string;
};

/**
 * Why the server wrote nothing. A field refusal names the input it is about
 * (`name`, `from` for side A, `to` for side B, `readers`, `zoneId`,
 * `entryId` or `destinations`) and says why in Odin's words; from a seed, it
 * also names the gateways whose entries it refuses.
 */
export type SiteLayoutRefusal =
  | { code: 'STALE_DRAFT' }
  | { code: 'NO_DRAFT' }
  | { code: 'REVISION_MISMATCH' }
  | { code: 'FIELD'; field: string; message: string; gatewayIds?: string[] }
  | { code: 'ZONE_IN_USE'; entryIds: string[] }
  | { code: 'SEED_NEEDS_DESTINATION'; gatewayIds: string[] }
  /** A publish refused until these gateways, linked to no entry, are seeded. */
  | { code: 'SEED_REQUIRED'; gatewayIds: string[] };

/**
 * A command whose answer was lost and that the client could not tell landed
 * or not (`STALLED`): nothing more is sent until the page is reloaded.
 */
export type SiteLayoutErrorCode = 'STALLED';

export class SiteLayoutError extends Error {
  constructor(public readonly code: SiteLayoutErrorCode, message: string) {
    super(message);
    this.name = 'SiteLayoutError';
  }
}
